import useDebouncedCallback from '@exodus/react-hooks/common/useDebouncedCallback';
import sendValidation from '@exodus/send-validation';
import type { VALIDATION_TYPES } from '@exodus/send-validation/src/constants';
import type { JSX } from 'react';
import { useState, useEffect } from 'react';

import createLogger from '@/utils/logger';

export type FormValidation<T> = {
  id: string;
  type: typeof VALIDATION_TYPES;
  shouldValidate?: (dependencies: T) => boolean;
  isValid: (dependencies: T) => Promise<boolean> | boolean;
  priority?: number;
  getMessage?: (dependencies: T) => string;
  getComponent?: (props: Record<string, unknown>) => JSX.Element;
  getTopComponent?: (props: Record<string, unknown>) => JSX.Element;
  getAction?: (dependencies: T) => {
    label: string;
    navigateTo: string;
    navigateToState?: Record<string, unknown>;
  };
};

export type FailedFormValidation<T> = FormValidation<T> & {
  message: string;
  bottomValidationComponent: JSX.Element;
  topValidationComponent: JSX.Element;
};

export type FormValidationHook<T> = (dependencies: T) => {
  hasError: boolean;
  failedValidation?: FailedFormValidation<T>;
};

export const createValidationHookWithDefaults = <T>({
  customValidations,
  globalValidations,
  customizations,
}: {
  customValidations?: FormValidation<T>[];
  globalValidations?: FormValidation<T>[];
  customizations?: Record<string, Partial<FormValidation<T>>>;
}) =>
  sendValidation.createSendValidation({
    customValidations,
    globalValidations,
    customizations,
    useDebouncedCallback,
    useEffect,
    useState,
    logger: createLogger('send-validation'),
  }).validationHook as FormValidationHook<T>;
